/**
 * Sticky add-to-cart for Fabric product pages.
 *
 * Clones the page's real add-to-cart button and floats the clone near the
 * bottom of the viewport once the original has been scrolled PAST (i.e. it has
 * left the viewport upwards). Scrolling back up so the original is visible
 * hides the clone again.
 *
 * Design notes:
 * - The clone is a deep copy of the live button node, so it inherits every
 *   theme class and therefore every computed style. Width and height are
 *   copied in pixels from the original's own box, which is what keeps it from
 *   becoming an edge-to-edge docked bar.
 * - Clicks are forwarded to the original button. Fabric's
 *   <add-to-cart-component> keeps ownership of cart + variant behaviour; none
 *   of that logic is duplicated here.
 * - The clone is kept in sync (label, disabled state, dimensions) because the
 *   variant picker rewrites the original button on every variant change.
 */
if (!customElements.get('sticky-atc')) {
  const MOBILE = '(max-width: 749px)';

  // The real button, in priority order. The first match is Fabric's product
  // form button; the others are defensive for app-injected or older markup.
  const BUTTON_SELECTORS = [
    'add-to-cart-component button[name="add"]',
    '.product-form-buttons button[name="add"]',
    'button.add-to-cart-button',
  ];

  // Surfaces that own the screen while open — a floating button over a cart
  // drawer or modal is a misclick waiting to happen.
  const BLOCKING_SELECTORS = [
    'dialog[open]',
    'cart-drawer-component[open]',
    '.cart-drawer[open]',
    '[aria-modal="true"]',
  ];

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.original = this.findOriginal();

      // Not a product page, or no purchasable button: stay inert and invisible.
      if (!this.original) return;

      this.hidden = false;
      this.clone = null;
      this.scrolledPast = false;

      this.applyOffset();
      this.buildClone();
      this.watchOriginal();
      this.watchMutations();
      this.watchViewport();
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.mediaQuery?.removeEventListener('change', this.onMediaChange);
      window.removeEventListener('resize', this.onResize);
      this.blockingTimer && clearInterval(this.blockingTimer);
    }

    findOriginal() {
      for (const selector of BUTTON_SELECTORS) {
        const el = document.querySelector(selector);
        if (el) return el;
      }
      return null;
    }

    /* ---------- geometry ---------- */

    applyOffset() {
      this.mediaQuery = window.matchMedia(MOBILE);
      this.onMediaChange = () => {
        this.applyOffset();
        this.syncDimensions();
      };
      this.mediaQuery.addEventListener('change', this.onMediaChange);

      const isMobile = this.mediaQuery.matches;
      const offset = isMobile
        ? this.dataset.offsetMobile || '16'
        : this.dataset.offsetDesktop || '24';

      this.style.setProperty('--sticky-atc-offset', `${offset}px`);
      this.style.setProperty('--sticky-atc-gutter', isMobile ? '16px' : '24px');
    }

    /**
     * Copy the original's real box. This is the requirement that the clone be
     * an exact replica rather than a full-width bar: we take the measured
     * pixel width, capped on wide screens so it never spans the page.
     */
    syncDimensions() {
      if (!this.clone || !this.original) return;

      const rect = this.original.getBoundingClientRect();
      if (!rect.width) return;

      const cap = Number(this.dataset.maxWidth) || 560;
      const width = Math.min(rect.width, cap);

      this.clone.style.width = `${Math.round(width)}px`;
      this.clone.style.height = `${Math.round(rect.height)}px`;
    }

    /* ---------- the clone ---------- */

    buildClone() {
      const clone = this.original.cloneNode(true);

      // An id must stay unique, and the clone must never be submitted as part
      // of the product form (it lives outside it, but be explicit).
      clone.removeAttribute('id');
      clone.removeAttribute('ref');
      clone.removeAttribute('name');
      clone.removeAttribute('on:click');
      clone.setAttribute('type', 'button');
      clone.classList.add('sticky-atc__clone');

      // The original is the accessible control; the clone is a shortcut to it.
      clone.setAttribute('aria-label', this.labelText() || 'Add to cart');
      clone.setAttribute('tabindex', '-1');

      clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      clone.querySelectorAll('[ref]').forEach((n) => n.removeAttribute('ref'));

      clone.addEventListener('click', (event) => {
        event.preventDefault();
        // Forward to the real button so the theme's own handler runs.
        this.original.click();
      });

      this.replaceChildren(clone);
      this.clone = clone;
      this.syncDimensions();
      this.syncState();
    }

    labelText() {
      const label = this.original.querySelector('.add-to-cart-text__content');
      return (label?.textContent || this.original.textContent || '').trim();
    }

    /** Mirror the original's label and availability onto the clone. */
    syncState() {
      if (!this.clone || !this.original) return;

      const disabled = this.original.hasAttribute('disabled');
      if (disabled) {
        this.clone.setAttribute('disabled', '');
      } else {
        this.clone.removeAttribute('disabled');
      }

      const from = this.original.querySelector('.add-to-cart-text__content');
      const to = this.clone.querySelector('.add-to-cart-text__content');
      if (from && to && to.textContent !== from.textContent) {
        to.textContent = from.textContent;
      }

      this.clone.setAttribute('aria-label', this.labelText() || 'Add to cart');
    }

    /* ---------- observers ---------- */

    /**
     * Visibility rule: show only when the original has left the viewport
     * UPWARDS (its bottom edge is above the top of the viewport). A button
     * still below the fold must not trigger the sticky version.
     */
    watchOriginal() {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.scrolledPast =
              !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          }
          this.render();
        },
        { threshold: 0 }
      );

      this.visibilityObserver.observe(this.original);
    }

    /** The variant picker swaps the button's label/disabled state in place. */
    watchMutations() {
      this.mutationObserver = new MutationObserver(() => {
        // The button node itself can be replaced wholesale on a variant change.
        const current = this.findOriginal();
        if (current && current !== this.original) {
          this.original = current;
          this.visibilityObserver.disconnect();
          this.watchOriginal();
          this.buildClone();
          return;
        }
        this.syncState();
        this.syncDimensions();
      });

      const scope =
        this.original.closest('add-to-cart-component') ||
        this.original.closest('.product-form-buttons') ||
        this.original.parentElement;

      if (scope) {
        this.mutationObserver.observe(scope, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'class'],
        });
      }
    }

    watchViewport() {
      this.onResize = () => this.syncDimensions();
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.dataset.hideWhenCartOpen === 'true') {
        // Drawer state lives in app/web-component internals with no public
        // event; a cheap poll is more robust than guessing at their API.
        this.blockingTimer = setInterval(() => this.render(), 300);
      }
    }

    blocked() {
      if (this.dataset.hideWhenCartOpen !== 'true') return false;
      return BLOCKING_SELECTORS.some((sel) => document.querySelector(sel));
    }

    render() {
      const show = this.scrolledPast && !this.blocked();
      if (show === this.shown) return;

      this.shown = show;
      this.classList.toggle('is-visible', show);

      if (show) this.syncDimensions();
    }
  }

  customElements.define('sticky-atc', StickyAtc);
}
